import { useMemo } from 'react';
import { useDebounce } from '@/shared/lib';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import type { CurrencyOption } from '../model';
import { useWithdrawCommission } from '@/entities/operation/api';

interface UseCommissionLogicParams {
  hasAmountError: boolean;
  selectedCurrency?: CurrencyOption;
  amountCurrency: string;
  amount: number;
  countryCode: string;
  onTop?: boolean;
}

export function useCommissionData({
  hasAmountError,
  selectedCurrency,
  amountCurrency,
  amount,
  countryCode,
  onTop = false,
}: UseCommissionLogicParams) {
  const debouncedAmount = useDebounce(amount, COMMISSION_CHECK_DELAY);
  const debouncedCountryCode = useDebounce<string>(
    countryCode,
    COMMISSION_CHECK_DELAY
  );
  const isAmountDebouncing = amount !== debouncedAmount;
  const isCountryCodeDebouncing = countryCode !== debouncedCountryCode;

  const commissionEnabled = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    if (isAmountDebouncing) {
      return false;
    }

    if (isCountryCodeDebouncing) {
      return false;
    }

    const amount = debouncedAmount;

    if (selectedCurrency?.min !== undefined && selectedCurrency?.min > amount) {
      return false;
    }

    const available = selectedCurrency?.available || 0;

    if (amount > available) {
      return false;
    }

    const hasValidDebouncedAmount = !!amount;
    const hasCurrency = !!amountCurrency;

    return hasValidDebouncedAmount && hasCurrency;
  }, [
    onTop,
    debouncedAmount,
    amountCurrency,
    hasAmountError,
    isAmountDebouncing,
    selectedCurrency?.available,
    selectedCurrency?.min,
    debouncedCountryCode,
    isCountryCodeDebouncing,
    countryCode,
    amount,
  ]);

  const commissionParams: Record<string, string | number> = {
    currencyCode: amountCurrency || '',
    amount: debouncedAmount || 0,
    bankCountryCode: countryCode || '',
    ontop: String(onTop),
  };

  const { commissionData, commissionError, isCommissionFetching } =
    useWithdrawCommission({
      operationType: 'bank',
      params: commissionParams,
      enabled: commissionEnabled,
    });

  const isGettingCommission = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    const hasAmount = !!amount;
    const hasCurrency = !!amountCurrency;

    return (
      (isAmountDebouncing && hasAmount && hasCurrency) ||
      isCommissionFetching ||
      (isCountryCodeDebouncing && hasAmount && hasCurrency && !!countryCode)
    );
  }, [
    amount,
    debouncedAmount,
    amountCurrency,
    hasAmountError,
    isAmountDebouncing,
    isCommissionFetching,
    debouncedCountryCode,
    isCountryCodeDebouncing,
    countryCode,
  ]);

  return {
    commissionData,
    commissionError,
    isGettingCommission,
  };
}
